/**
 * Date Utility Functions
 * Comprehensive date and time utilities for appointment management and scheduling
 */

class DateUtil {
  // Constants for common formats
  static DATE_FORMATS = {
    ISO: 'YYYY-MM-DD',
    ISO_DATETIME: 'YYYY-MM-DDTHH:mm:ss.sssZ',
    US_SHORT: 'M/D/YYYY',
    US_LONG: 'dddd, MMMM D, YYYY',
    TIME_24: 'HH:mm',
    TIME_12: 'h:mm A'
  };

  static DAY_NAMES = [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
  ];

  static MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  static MONTH_NAMES_SHORT = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  // =====================
  // CORE DATE FUNCTIONS
  // =====================

  /**
   * Get current date as Date object
   */
  static now() {
    return new Date();
  }

  /**
   * Get current date at start of day (00:00:00)
   */
  static today() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  /**
   * Get tomorrow's date at start of day
   */
  static tomorrow() {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  /**
   * Get yesterday's date at start of day
   */
  static yesterday() {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  /**
   * Create date from string with validation
   */
  static createDate(dateString) {
    if (!dateString) return null;
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return null;
    }
    return date;
  }

  /**
   * Create date from components
   */
  static createDateFromComponents(year, month, day, hours = 0, minutes = 0, seconds = 0) {
    return new Date(year, month - 1, day, hours, minutes, seconds); // month is 0-indexed
  }

  // =====================
  // DATE FORMATTING
  // =====================

  /**
   * Format date for display (US long format)
   * @param {Date|string} date - Date to format
   * @returns {string} - "Monday, January 15, 2025"
   */
  static formatDateLong(date) {
    if (!date) return '';
    
    const dateObj = date instanceof Date ? date : this.createDate(date);
    if (!dateObj) return '';

    return dateObj.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  /**
   * Format date for display (US short format)
   * @param {Date|string} date - Date to format
   * @returns {string} - "Mon, Jan 15, 2025"
   */
  static formatDateShort(date) {
    if (!date) return '';
    
    const dateObj = date instanceof Date ? date : this.createDate(date);
    if (!dateObj) return '';

    return dateObj.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  /**
   * Format date for input fields (ISO format)
   * @param {Date|string} date - Date to format
   * @returns {string} - "2025-01-15"
   */
  static formatDateISO(date) {
    if (!date) return '';
    
    const dateObj = date instanceof Date ? date : this.createDate(date);
    if (!dateObj) return '';

    return dateObj.toISOString().split('T')[0];
  }

  /**
   * Format date and time for display
   * @param {Date|string} date - Date to format
   * @param {string} time - Time string (HH:mm format)
   * @returns {string} - "Monday, January 15, 2025 at 2:30 PM"
   */
  static formatDateTime(date, time) {
    if (!date) return '';
    
    const formattedDate = this.formatDateLong(date);
    if (!time) return formattedDate;
    
    const formattedTime = this.formatTime(time);
    return `${formattedDate} at ${formattedTime}`;
  }

  /**
   * Format relative date (Today, Tomorrow, date)
   * @param {Date|string} date - Date to format
   * @returns {string} - "Today", "Tomorrow", or formatted date
   */
  static formatDateRelative(date) {
    if (!date) return '';
    
    const dateObj = date instanceof Date ? date : this.createDate(date);
    if (!dateObj) return '';

    const today = this.today();
    const tomorrow = this.tomorrow();
    const yesterday = this.yesterday();
    
    // Reset time for comparison
    const compareDate = new Date(dateObj);
    compareDate.setHours(0, 0, 0, 0);
    
    if (compareDate.getTime() === today.getTime()) {
      return 'Today';
    } else if (compareDate.getTime() === tomorrow.getTime()) {
      return 'Tomorrow';
    } else if (compareDate.getTime() === yesterday.getTime()) {
      return 'Yesterday';
    } else {
      return this.formatDateShort(dateObj);
    }
  }

  // =====================
  // TIME FORMATTING
  // =====================

  /**
   * Format time to 12-hour format with AM/PM
   * @param {string} timeString - Time in HH:mm or HH:mm:ss format
   * @returns {string} - "2:30 PM"
   */
  static formatTime(timeString) {
    if (!timeString) return '';
    
    // Handle different time formats
    let time;
    if (timeString.includes(':')) {
      // Format: "14:30" or "14:30:00"
      const [hours, minutes] = timeString.split(':');
      time = new Date();
      time.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    } else {
      // Try to parse as is
      time = new Date(timeString);
    }
    
    if (isNaN(time.getTime())) return '';
    
    return time.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  /**
   * Format time to 24-hour format
   * @param {string} timeString - Time in various formats
   * @returns {string} - "14:30"
   */
  static formatTime24(timeString) {
    if (!timeString) return '';
    
    const time = this.parseTimeString(timeString);
    if (!time) return '';
    
    const hours = time.getHours().toString().padStart(2, '0');
    const minutes = time.getMinutes().toString().padStart(2, '0');
    
    return `${hours}:${minutes}`;
  }

  /**
   * Parse time string to Date object
   * @param {string} timeString - Time string in various formats
   * @returns {Date|null} - Date object or null if invalid
   */
  static parseTimeString(timeString) {
    if (!timeString) return null;
    
    let time;
    if (timeString.includes(':')) {
      // Format: "14:30" or "14:30:00"
      const [hours, minutes] = timeString.split(':');
      time = new Date();
      time.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    } else {
      // Try to parse as is
      time = new Date(timeString);
    }
    
    return isNaN(time.getTime()) ? null : time;
  }

  // =====================
  // DATE CALCULATIONS
  // =====================

  /**
   * Add days to a date
   * @param {Date|string} date - Starting date
   * @param {number} days - Number of days to add (can be negative)
   * @returns {Date} - New date
   */
  static addDays(date, days) {
    const dateObj = date instanceof Date ? new Date(date) : this.createDate(date);
    if (!dateObj) return null;
    
    dateObj.setDate(dateObj.getDate() + days);
    return dateObj;
  }

  /**
   * Add hours to a date
   * @param {Date|string} date - Starting date
   * @param {number} hours - Number of hours to add (can be negative)
   * @returns {Date} - New date
   */
  static addHours(date, hours) {
    const dateObj = date instanceof Date ? new Date(date) : this.createDate(date);
    if (!dateObj) return null;
    
    dateObj.setHours(dateObj.getHours() + hours);
    return dateObj;
  }

  /**
   * Add minutes to a date
   * @param {Date|string} date - Starting date
   * @param {number} minutes - Number of minutes to add (can be negative)
   * @returns {Date} - New date
   */
  static addMinutes(date, minutes) {
    const dateObj = date instanceof Date ? new Date(date) : this.createDate(date);
    if (!dateObj) return null;
    
    dateObj.setMinutes(dateObj.getMinutes() + minutes);
    return dateObj;
  }

  /**
   * Get difference between two dates in days
   * @param {Date|string} date1 - First date
   * @param {Date|string} date2 - Second date
   * @returns {number} - Difference in days (can be negative)
   */
  static diffDays(date1, date2) {
    const d1 = date1 instanceof Date ? date1 : this.createDate(date1);
    const d2 = date2 instanceof Date ? date2 : this.createDate(date2);
    
    if (!d1 || !d2) return 0;
    
    const diffMs = d2.getTime() - d1.getTime();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  }

  /**
   * Get difference between two dates in hours
   * @param {Date|string} date1 - First date
   * @param {Date|string} date2 - Second date
   * @returns {number} - Difference in hours (can be negative)
   */
  static diffHours(date1, date2) {
    const d1 = date1 instanceof Date ? date1 : this.createDate(date1);
    const d2 = date2 instanceof Date ? date2 : this.createDate(date2);
    
    if (!d1 || !d2) return 0;
    
    const diffMs = d2.getTime() - d1.getTime();
    return diffMs / (1000 * 60 * 60);
  }

  /**
   * Get difference between two dates in minutes
   * @param {Date|string} date1 - First date
   * @param {Date|string} date2 - Second date
   * @returns {number} - Difference in minutes (can be negative)
   */
  static diffMinutes(date1, date2) {
    const d1 = date1 instanceof Date ? date1 : this.createDate(date1);
    const d2 = date2 instanceof Date ? date2 : this.createDate(date2);
    
    if (!d1 || !d2) return 0;
    
    const diffMs = d2.getTime() - d1.getTime();
    return Math.round(diffMs / (1000 * 60));
  }

  // =====================
  // TIME DURATION
  // =====================

  /**
   * Calculate duration between start and end time
   * @param {string} startTime - Start time (HH:mm format)
   * @param {string} endTime - End time (HH:mm format)
   * @returns {string} - Formatted duration ("1 hour 30 minutes")
   */
  static calculateDuration(startTime, endTime) {
    if (!startTime || !endTime) return '';
    
    const start = this.parseTimeString(startTime);
    const end = this.parseTimeString(endTime);
    
    if (!start || !end) return '';
    
    // Calculate difference in minutes
    const diffMinutes = this.diffMinutes(start, end);
    
    return this.formatDuration(diffMinutes);
  }

  /**
   * Format duration in minutes to human readable string
   * @param {number} minutes - Duration in minutes
   * @returns {string} - "1 hour 30 minutes" or "45 minutes"
   */
  static formatDuration(minutes) {
    if (!minutes || minutes <= 0) return '';
    
    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    } else {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      
      if (remainingMinutes === 0) {
        return `${hours} hour${hours !== 1 ? 's' : ''}`;
      } else {
        return `${hours} hour${hours !== 1 ? 's' : ''} ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}`;
      }
    }
  }

  /**
   * Convert duration string to minutes
   * @param {string} durationString - Duration like "1 hour 30 minutes"
   * @returns {number} - Total minutes
   */
  static parseDurationToMinutes(durationString) {
    if (!durationString) return 0;
    
    const hourMatch = durationString.match(/(\d+)\s*hour/);
    const minuteMatch = durationString.match(/(\d+)\s*minute/);
    
    const hours = hourMatch ? parseInt(hourMatch[1]) : 0;
    const minutes = minuteMatch ? parseInt(minuteMatch[1]) : 0;
    
    return (hours * 60) + minutes;
  }

  // =====================
  // DATE RANGES
  // =====================

  /**
   * Get start of week (Monday)
   * @param {Date|string} date - Reference date
   * @returns {Date} - Start of week
   */
  static getStartOfWeek(date = new Date()) {
    const dateObj = date instanceof Date ? new Date(date) : this.createDate(date);
    if (!dateObj) return null;
    
    const dayOfWeek = dateObj.getDay();
    const diff = dateObj.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Adjust for Sunday
    
    const startOfWeek = new Date(dateObj);
    startOfWeek.setDate(diff);
    startOfWeek.setHours(0, 0, 0, 0);
    
    return startOfWeek;
  }

  /**
   * Get end of week (Sunday)
   * @param {Date|string} date - Reference date
   * @returns {Date} - End of week
   */
  static getEndOfWeek(date = new Date()) {
    const startOfWeek = this.getStartOfWeek(date);
    if (!startOfWeek) return null;
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);
    
    return endOfWeek;
  }

  /**
   * Get start of month
   * @param {Date|string} date - Reference date
   * @returns {Date} - Start of month
   */
  static getStartOfMonth(date = new Date()) {
    const dateObj = date instanceof Date ? new Date(date) : this.createDate(date);
    if (!dateObj) return null;
    
    return new Date(dateObj.getFullYear(), dateObj.getMonth(), 1);
  }

  /**
   * Get end of month
   * @param {Date|string} date - Reference date
   * @returns {Date} - End of month
   */
  static getEndOfMonth(date = new Date()) {
    const dateObj = date instanceof Date ? new Date(date) : this.createDate(date);
    if (!dateObj) return null;
    
    return new Date(dateObj.getFullYear(), dateObj.getMonth() + 1, 0, 23, 59, 59, 999);
  }

  /**
   * Get date range based on filter type
   * @param {string} filter - Filter type ('today', 'tomorrow', 'week', 'month')
   * @param {Date} referenceDate - Reference date for calculations
   * @returns {Object} - {start, end} dates
   */
  static getDateRange(filter, referenceDate = new Date()) {
    const today = this.today();
    
    switch (filter) {
      case 'today':
        return {
          start: this.formatDateISO(today),
          end: this.formatDateISO(today)
        };
        
      case 'tomorrow':
        const tomorrow = this.tomorrow();
        return {
          start: this.formatDateISO(tomorrow),
          end: this.formatDateISO(tomorrow)
        };
        
      case 'week':
        const startOfWeek = this.getStartOfWeek(referenceDate);
        const endOfWeek = this.getEndOfWeek(referenceDate);
        return {
          start: this.formatDateISO(startOfWeek),
          end: this.formatDateISO(endOfWeek)
        };
        
      case 'month':
        const startOfMonth = this.getStartOfMonth(referenceDate);
        const endOfMonth = this.getEndOfMonth(referenceDate);
        return {
          start: this.formatDateISO(startOfMonth),
          end: this.formatDateISO(endOfMonth)
        };
        
      default:
        return { start: null, end: null };
    }
  }

  // =====================
  // DATE VALIDATION
  // =====================

  /**
   * Check if date is valid
   * @param {Date|string} date - Date to validate
   * @returns {boolean} - True if valid
   */
  static isValidDate(date) {
    if (!date) return false;
    
    const dateObj = date instanceof Date ? date : this.createDate(date);
    return dateObj && !isNaN(dateObj.getTime());
  }

  /**
   * Check if date is in the past
   * @param {Date|string} date - Date to check
   * @returns {boolean} - True if in past
   */
  static isPastDate(date) {
    if (!this.isValidDate(date)) return false;
    
    const dateObj = date instanceof Date ? date : this.createDate(date);
    const today = this.today();
    
    return dateObj < today;
  }

  /**
   * Check if date is today
   * @param {Date|string} date - Date to check
   * @returns {boolean} - True if today
   */
  static isToday(date) {
    if (!this.isValidDate(date)) return false;
    
    const dateObj = date instanceof Date ? date : this.createDate(date);
    const today = this.today();
    
    return dateObj.toDateString() === today.toDateString();
  }

  /**
   * Check if date is weekend
   * @param {Date|string} date - Date to check
   * @returns {boolean} - True if weekend (Saturday or Sunday)
   */
  static isWeekend(date) {
    if (!this.isValidDate(date)) return false;
    
    const dateObj = date instanceof Date ? date : this.createDate(date);
    const dayOfWeek = dateObj.getDay();
    
    return dayOfWeek === 0 || dayOfWeek === 6; // Sunday or Saturday
  }

  /**
   * Get day of week
   * @param {Date|string} date - Date to check
   * @returns {number} - Day of week (0=Sunday, 1=Monday, etc.)
   */
  static getDayOfWeek(date) {
    if (!this.isValidDate(date)) return -1;
    
    const dateObj = date instanceof Date ? date : this.createDate(date);
    return dateObj.getDay();
  }

  /**
   * Get day name
   * @param {Date|string} date - Date to check
   * @returns {string} - Day name ("Monday", "Tuesday", etc.)
   */
  static getDayName(date) {
    const dayOfWeek = this.getDayOfWeek(date);
    return dayOfWeek >= 0 ? this.DAY_NAMES[dayOfWeek] : '';
  }

  // =====================
  // BUSINESS/SCHEDULE HELPERS
  // =====================

  /**
   * Generate time slots for a time range
   * @param {string} startTime - Start time (HH:mm)
   * @param {string} endTime - End time (HH:mm)
   * @param {number} intervalMinutes - Interval between slots in minutes (default: 30)
   * @returns {Array} - Array of time slots
   */
  static generateTimeSlots(startTime, endTime, intervalMinutes = 30) {
    const slots = [];
    
    if (!startTime || !endTime) return slots;
    
    const start = this.parseTimeString(startTime);
    const end = this.parseTimeString(endTime);
    
    if (!start || !end) return slots;
    
    const current = new Date(start);
    
    while (current < end) {
      const timeString = this.formatTime24(current.toTimeString());
      slots.push({
        time: timeString,
        display: this.formatTime(timeString)
      });
      
      current.setMinutes(current.getMinutes() + intervalMinutes);
    }
    
    return slots;
  }

  /**
   * Check if a time slot can accommodate a duration
   * @param {string} startTime - Start time (HH:mm)
   * @param {number} durationMinutes - Required duration in minutes
   * @param {string} endLimit - End time limit (HH:mm)
   * @returns {boolean} - True if slot can accommodate duration
   */
  static canAccommodateDuration(startTime, durationMinutes, endLimit) {
    if (!startTime || !endLimit || !durationMinutes) return false;
    
    const start = this.parseTimeString(startTime);
    const limit = this.parseTimeString(endLimit);
    
    if (!start || !limit) return false;
    
    const requiredEnd = this.addMinutes(start, durationMinutes);
    return requiredEnd <= limit;
  }

  /**
   * Get time slots that can accommodate consecutive services
   * @param {Array} timeSlots - Available time slots
   * @param {number} totalDurationMinutes - Total duration needed
   * @param {number} slotInterval - Interval between slots (default: 30)
   * @returns {Array} - Time slots that can accommodate the duration
   */
  static getConsecutiveTimeSlots(timeSlots, totalDurationMinutes, slotInterval = 30) {
    if (!timeSlots || !totalDurationMinutes) return [];
    
    const slotsNeeded = Math.ceil(totalDurationMinutes / slotInterval);
    const validSlots = [];
    
    for (let i = 0; i <= timeSlots.length - slotsNeeded; i++) {
      const startSlot = timeSlots[i];
      const endSlotIndex = i + slotsNeeded - 1;
      
      // Check if all required slots are consecutive
      let isConsecutive = true;
      for (let j = i; j < i + slotsNeeded - 1; j++) {
        const currentTime = this.parseTimeString(timeSlots[j].time);
        const nextTime = this.parseTimeString(timeSlots[j + 1].time);
        
        if (!currentTime || !nextTime) {
          isConsecutive = false;
          break;
        }
        
        const expectedNext = this.addMinutes(currentTime, slotInterval);
        if (Math.abs(this.diffMinutes(expectedNext, nextTime)) > 1) { // Allow 1 minute tolerance
          isConsecutive = false;
          break;
        }
      }
      
      if (isConsecutive) {
        validSlots.push(startSlot);
      }
    }
    
    return validSlots;
  }

  // =====================
  // UTILITY FUNCTIONS
  // =====================

  /**
   * Format time slot for display
   * @param {Object} slot - Time slot with startTime and endTime
   * @returns {string} - Formatted time slot
   */
  static formatTimeSlot(slot) {
    if (!slot || !slot.startTime || !slot.endTime) return '';
    
    const startFormatted = this.formatTime(slot.startTime);
    const endFormatted = this.formatTime(slot.endTime);
    
    return `${startFormatted} - ${endFormatted}`;
  }

  /**
   * Get current timestamp in ISO format
   * @returns {string} - ISO timestamp
   */
  static getTimestamp() {
    return new Date().toISOString();
  }

  /**
   * Convert timezone offset to string
   * @param {number} offsetMinutes - Timezone offset in minutes
   * @returns {string} - Timezone string like "+05:30" or "-08:00"
   */
  static formatTimezoneOffset(offsetMinutes) {
    const sign = offsetMinutes <= 0 ? '+' : '-';
    const absOffset = Math.abs(offsetMinutes);
    const hours = Math.floor(absOffset / 60);
    const minutes = absOffset % 60;
    
    return `${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  /**
   * Get user's timezone offset
   * @returns {string} - User's timezone offset
   */
  static getUserTimezoneOffset() {
    const offsetMinutes = new Date().getTimezoneOffset();
    return this.formatTimezoneOffset(offsetMinutes);
  }
}

// Export for both module and global usage
export default DateUtil;

// Make available globally for backward compatibility
if (typeof window !== 'undefined') {
  window.DateUtil = DateUtil;
}
